import React, { useEffect, useState, useContext, useMemo } from 'react';
import {
    Card,
    Col,
    Row,
    Button,
    Typography,
    Tag,
    message,
    Layout,
    Select,
    Avatar,
    Statistic,
    Spin,
    Empty,
    Tooltip,
    Modal,
    Table,
    Progress
} from 'antd';
import {
    ReloadOutlined,
    UserSwitchOutlined,
    UserOutlined,
    CheckCircleOutlined,
    ReadOutlined,
    SettingOutlined,
    AppstoreOutlined,
    DragOutlined,
    UndoOutlined
} from '@ant-design/icons';
import { AuthContext } from "../../config/AuthContext";
import { useNavigate } from "react-router-dom";
import AppSider from "../avaluacio/components/AppSider";
import Configuracio from "./projectes/content/Configuracio";

// DAOs
import { GetUserAules } from "../../dao/abp/GetUserAules";
import { GetProjectes } from "../../dao/abp/GetProjectes";
import { GetAulaValidacions } from "../../dao/abp/GetAulaValidacions";
import { GetAlumnesValidacioByProjectV2 } from "../../dao/abp/GetAlumnesValidacioByProjectV2";
import { GetRankingsUsuariId } from "../../dao/jutge/GetRankingsUsuariId";
import { GetAlumneId } from "../../dao/GetAlumneId";
import { DefaultAulumneInfo } from "../../model/AlumneInfo";
import { GetAlumneAulesId } from "../../dao/abp/GetAlumnesAulesId";
import { GetClassroomLayout, PostClassroomLayout } from "../../dao/jutge/ClassroomLayoutDao";

import AvaluacioColors from "../../styles/AvaluacioColors";
import { GetGrups } from '../../dao/abp/GetGrups';

// --- Helper Functions ---
const convertToNota = (val: number, escala: number): number => {
    let points: [number, number][];
    if (escala === 3) {
        points = [[0, 2], [1, 2], [2, 8], [3, 10]];
    } else if (escala === 5) {
        points = [[0, 2], [2, 2], [3, 5], [4, 8], [5, 10]];
    } else if (escala === 10) {
        points = [[0, 2], [4, 2], [5, 5], [6, 6], [7, 7], [8, 8], [9, 9], [10, 10]];
    } else {
        return escala > 0 ? (val / escala) * 10 : 0;
    }
    if (val <= points[0][0]) return points[0][1];
    if (val >= points[points.length - 1][0]) return points[points.length - 1][1];
    for (let i = 0; i < points.length - 1; i++) {
        const [x0, y0] = points[i];
        const [x1, y1] = points[i + 1];
        if (val >= x0 && val <= x1) {
            if (x1 === x0) return y0;
            return y0 + ((val - x0) / (x1 - x0)) * (y1 - y0);
        }
    }
    return 0;
};

const getEffectiveNota = (item: any): number => {
    const escalaValidacio = item.escala_validacio || 5;
    if (item.rubrica?.length > 0) {
        const totalPes = item.rubrica.reduce((acc: number, r: any) => acc + (r.pes || 0), 0);
        const weightedSum = item.rubrica.reduce((acc: number, r: any) => acc + ((r.avaluacio || 0) * (r.pes || 0)), 0);
        const avg = totalPes > 0 ? weightedSum / totalPes : 0;
        return convertToNota(avg, escalaValidacio);
    }
    return convertToNota(item.avaluacio || 0, escalaValidacio);
};

const { Title, Text } = Typography;
const { Content } = Layout;
const { Option } = Select;

function getAvatarSrc(img: string | undefined | null) {
    if (!img || img === "undefined" || img === "null") return undefined;
    if (img.startsWith("http") || img.startsWith("data:") || img.startsWith("/")) return img;
    if (img.startsWith("base64:")) img = img.split(":").pop()!;
    if (img.startsWith("/9j/")) return `data:image/jpeg;base64,${img}`;
    if (img.startsWith("iVB")) return `data:image/png;base64,${img}`;
    return `data:image/png;base64,${img}`;
}

const stringToColor = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const c = (hash & 0x00FFFFFF).toString(16).toUpperCase();
    return '#' + '00000'.substring(0, 6 - c.length) + c;
};

// --- Interfaces ---

interface StudentData {
    id: number;
    nom: string;
    cognoms: string;
    avatar?: string;
    // Computed fields
    averageGrade?: number;
    projectGrades?: { id: number; nom: string; nota: number | null; estat: number }[];
    raGrades?: { nom: string; nota: number | null }[];
}

interface ClassroomData {
    alumnes: StudentData[];
    projectes: any[];
}

// --- Helper Components ---

// Stacked Bar Chart for Projects
const ProjectStackedBarChart: React.FC<{ students: StudentData[], projects: any[] }> = ({ students, projects }) => {
    // Ranges 0-10 scale.
    const ranges = [
        { label: 'Excelente (9-10)', min: 9, max: 11, color: '#52c41a', textColor: '#237804' },
        { label: 'Notable (7-8.9)', min: 7, max: 9, color: '#1890ff', textColor: '#0050b3' },
        { label: 'Bien (5-6.9)', min: 5, max: 7, color: '#faad14', textColor: '#ad6800' },
        { label: 'Suspenso (0-4.9)', min: 0, max: 5, color: '#f5222d', textColor: '#a8071a' },
    ];

    if (projects.length === 0) return <Empty description="Sin proyectos en esta aula" />;

    return (
        <div style={{ padding: '10px 0', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Chart Area */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 16, paddingBottom: 24, borderBottom: '1px solid #f0f0f0', overflowX: 'auto' }}>
                {projects.map(proj => {
                    // Calculate distribution for this Project
                    const counts = ranges.map(r => ({ ...r, count: 0 }));

                    let validGradesCount = 0;

                    students.forEach(s => {
                        const pGrade = s.projectGrades?.find(pg => pg.id === proj.id);
                        const val = pGrade && pGrade.nota !== null ? pGrade.nota : null;

                        if (val !== null) {
                            const range = counts.find(r => val >= r.min && val < r.max);
                            if (range) {
                                range.count++;
                                validGradesCount++;
                            }
                        }
                    });

                    const total = validGradesCount || 1; // Avoid div by 0 for percentage calcs

                    return (
                        <div key={proj.id} style={{ flex: 1, minWidth: 60, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center' }}>
                            {/* The Bar */}
                            <div style={{ width: '100%', maxWidth: 40, height: '100%', background: '#f5f5f5', borderRadius: 4, overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                                {validGradesCount === 0 ? (
                                    <div style={{ height: '4px', background: '#d9d9d9', width: '100%' }} />
                                ) : (
                                    /* Segments - Rendered standard (Top: Excelente, Bottom: Suspenso). Flex direction column */
                                    counts.map((range, idx) => {
                                        const percent = (range.count / total) * 100;
                                        if (percent === 0) return null;
                                        return (
                                            <Tooltip key={idx} title={`${range.label}: ${range.count} alumnos (${percent.toFixed(0)}%)`}>
                                                <div style={{
                                                    height: `${percent}%`,
                                                    background: range.color,
                                                    width: '100%',
                                                    transition: 'height 0.3s'
                                                }} />
                                            </Tooltip>
                                        );
                                    })
                                )}
                            </div>
                            <Tooltip title={proj.nom}>
                                <div style={{ marginTop: 8, fontSize: 12, fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 60 }}>
                                    {proj.nom}
                                </div>
                            </Tooltip>
                        </div>
                    );
                })}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
                {ranges.map(r => (
                    <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 10, height: 10, backgroundColor: r.color, borderRadius: 2 }} />
                        <Text style={{ fontSize: 11, color: '#666' }}>{r.label.split(' ')[0]}</Text>
                    </div>
                ))}
            </div>
        </div>
    );
};

// --- Student Detail Modal ---

const StudentDetailModal: React.FC<{
    visible: boolean;
    onClose: () => void;
    student: StudentData | null;
}> = ({ visible, onClose, student }) => {

    if (!student) return null;

    // Use computed grades from student object
    const projectGrades = student.projectGrades || [];

    // Columns for Project Table
    const columns = [
        { title: 'Proyecto', dataIndex: 'nom', key: 'nom', render: (text: string) => <b>{text}</b> },
        {
            title: 'Nota',
            dataIndex: 'nota',
            key: 'nota',
            align: 'right' as const,
            render: (val: number | null) => {
                if (val === null) return <Tag>N/P</Tag>;
                const color = val >= 5 ? 'green' : 'red';
                return (
                    <Tag color={color} style={{ fontSize: 14, padding: '4px 10px' }}>{val.toFixed(2)}</Tag>
                );
            }
        },
        {
            title: 'Nota Final',
            dataIndex: 'nota',
            key: 'nota_final',
            align: 'center' as const,
            render: (val: number | null) => {
                if (val === null) return <Tag>N/P</Tag>;
                const finalVal = val < 5 ? Math.floor(val) : Math.round(val);
                const color = finalVal >= 5 ? 'green' : 'red';
                return (
                    <Tag color={color} style={{ fontSize: 14, padding: '4px 10px', fontWeight: 'bold' }}>{finalVal}</Tag>
                );
            }
        },
        {
            title: 'Estado',
            dataIndex: 'estat',
            key: 'estat',
            render: (estat: number) => {
                let color = 'default';
                let text = 'Desconocido';
                if (estat === 1) { color = 'green'; text = 'En curso'; }
                if (estat === 2) { color = 'orange'; text = 'Finalizado'; }
                if (estat === 3) { color = 'red'; text = 'Cerrado'; }
                return <Tag color={color}>{text}</Tag>;
            }
        }
    ];

    const studentAvatar = getAvatarSrc(student.avatar);

    return (
        <Modal
            title={<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Avatar src={studentAvatar} size="large">{student.nom[0]}</Avatar>
                <div>
                    <div style={{ fontWeight: 'bold' }}>{student.nom} {student.cognoms}</div>
                    <div style={{ fontSize: 12, fontWeight: 'normal', color: '#666' }}>Detalle de Calificaciones (Sobre 10)</div>
                </div>
            </div>}
            open={visible}
            onCancel={onClose}
            footer={[<Button key="close" onClick={onClose}>Cerrar</Button>]}
            width={700}
        >
            <Table
                columns={columns}
                dataSource={projectGrades}
                rowKey="id"
                pagination={false}
                bordered
            />
        </Modal>
    );
};


// --- Dashboard Component ---

const Dashboard = () => {
    const navigate = useNavigate();
    const { userId, setLoggedIn, setUserId, setUser, setAlumneInfo, setUserPermisos, setBreadCrumb, user, alumne } = useContext(AuthContext);

    // Common State
    const [viewMode, setViewMode] = useState<'dashboard' | 'config'>('dashboard');
    const [classroomViewMode, setClassroomViewMode] = useState<'aula' | 'graella'>('aula');
    const [cardPositions, setCardPositions] = useState<Record<string, { x: number; y: number }>>({});
    const [savingLayout, setSavingLayout] = useState(false);
    const [loadingData, setLoadingData] = useState(false);

    // Admin State
    const [aulas, setAulas] = useState<{ id: number; nom: string }[]>([]);
    const [selectedAula, setSelectedAula] = useState<number | null>(null);
    const [classroomData, setClassroomData] = useState<ClassroomData | null>(null);
    const [avatars, setAvatars] = useState<Record<string, string>>({});
    const [selectedStudent, setSelectedStudent] = useState<StudentData | null>(null);
    const [modalVisible, setModalVisible] = useState(false);

    // Student State
    const [myProjects, setMyProjects] = useState<any[]>([]);
    const [myProjectGrades, setMyProjectGrades] = useState<{ id: number; nom: string; nota: number | null; estat: number }[]>([]);
    const [myAverageGrade, setMyAverageGrade] = useState<number>(0);

    useEffect(() => {
        setBreadCrumb([{ title: "Avaluació" }, { title: "Dashboard" }], true);

        if (user.tipus === "1" || user.tipus === "0") {
            fetchClassrooms();
            fetchAvatars();
        } else {
            fetchStudentData();
            fetchAvatars();
        }
    }, [user]);

    useEffect(() => {
        if (selectedAula && (user.tipus === "1" || user.tipus === "0")) {
            fetchClassroomStats(selectedAula);
        } else {
            setClassroomData(null);
            setCardPositions({});
        }
    }, [selectedAula]);


    // --- API Calls ---

    const fetchClassrooms = async () => {
        try {
            const userIdStr = userId ? userId.toString() : "";
            const [aulesData, allProjects] = await Promise.all([
                GetUserAules(userIdStr),
                GetProjectes(userIdStr)
            ]);

            if (Array.isArray(aulesData) && Array.isArray(allProjects)) {
                // Filter classrooms: only include those with at least one Active (1) or Finalized (2) project
                let filteredAulas = aulesData.filter(aula => {
                    return allProjects.some((p: any) => {
                        const projectAulas = String(p.id_aula || '').split(',').map(id => id.trim());
                        return projectAulas.includes(String(aula.id)) && (p.estat === 1 || p.estat === 2);
                    });
                });

                // Sort by ID descending (newest first)
                filteredAulas = filteredAulas.sort((a: any, b: any) => b.id - a.id);

                setAulas(filteredAulas);
                if (filteredAulas.length > 0) {
                    setSelectedAula(filteredAulas[0].id);
                } else if (filteredAulas.length === 0 && aulesData.length > 0) {
                    setSelectedAula(null);
                }
            }
        } catch (error) {
            console.error(error);
            message.error("Error cargando aulas");
        }
    };

    const fetchClassroomStats = async (aulaId: number) => {
        setLoadingData(true);
        try {
            const userIdStr = userId ? userId.toString() : "";
            const [allProjects, layout] = await Promise.all([
                GetProjectes(userIdStr),
                GetClassroomLayout(aulaId)
            ]);

            if (layout && typeof layout === 'object' && Object.keys(layout).length > 0) {
                setCardPositions(layout);
            } else {
                setCardPositions({});
            }

            const aulaProjects = Array.isArray(allProjects)
                ? allProjects.filter((p: any) => {
                    const projectAulas = String(p.id_aula || '').split(',').map(id => id.trim());
                    return projectAulas.includes(String(aulaId));
                })
                : [];

            if (aulaProjects.length === 0) {
                setClassroomData({ alumnes: [], projectes: [] });
                setLoadingData(false);
                return;
            }

            // 2. Fetch all valid students belonging to this specific classroom
            const validStudents = await GetAlumneAulesId(aulaId.toString());
            const validStudentIds = new Set<number>();
            if (Array.isArray(validStudents)) {
                validStudents.forEach((student: any) => {
                    validStudentIds.add(Number(student.id));
                });
            }

            // 3. Fetch Groups and Students for these projects, filtering by selected classroom
            const studentMap = new Map<number, StudentData>();

            for (const proj of aulaProjects) {
                const groups = await GetGrups(proj.id);
                if (Array.isArray(groups)) {
                    for (const g of groups) {
                        if (Array.isArray(g.alumnes2)) {
                            for (const s of g.alumnes2) {
                                if (validStudentIds.has(s.id) && !studentMap.has(s.id)) {
                                    studentMap.set(s.id, {
                                        id: s.id,
                                        nom: s.nom,
                                        cognoms: s.cognoms,
                                        avatar: s.avatar
                                    });
                                }
                            }
                        }
                    }
                }
            }

            const alumnes = Array.from(studentMap.values());

            // Fetch RA data per student per project using V2
            const raPromises: Promise<{ studentId: number, data: any }>[] = [];
            alumnes.forEach(s => {
                raPromises.push(
                    GetAlumnesValidacioByProjectV2("0", s.id.toString())
                        .then(data => ({ studentId: s.id, data }))
                );
            });
            const raResults = await Promise.all(raPromises);

            // Group RA data and compute Project Grades
            alumnes.forEach(s => {
                const sResult = raResults.find(r => r.studentId === s.id);
                const raGrades: { nom: string, nota: number | null }[] = [];
                const projectGradesMap = new Map<number, { sum: number, count: number }>();

                if (sResult && Array.isArray(sResult.data)) {
                    sResult.data.forEach((v: any) => {
                        if (v.ranom) {
                            const raNota = v.nota !== null && v.nota !== undefined && v.nota !== "" ? Number(v.nota) : 0;
                            const raNameMatch = v.ranom.match(/^(RA\d+)/);
                            const raName = raNameMatch ? raNameMatch[1] : v.ranom;

                            raGrades.push({
                                nom: raName,
                                nota: raNota
                            });

                            let parsedProjs = v.projectes;
                            if (typeof v.projectes === 'string') {
                                try {
                                    parsedProjs = JSON.parse(v.projectes);
                                } catch (e) {
                                    parsedProjs = [];
                                }
                            }

                            if (Array.isArray(parsedProjs)) {
                                parsedProjs.forEach((p: any) => {
                                    const projId = Number(p.id_projecte);
                                    const projNota = p.nota !== null && p.nota !== undefined && p.nota !== "" ? Number(p.nota) : 0;
                                    const current = projectGradesMap.get(projId) || { sum: 0, count: 0 };
                                    projectGradesMap.set(projId, {
                                        sum: current.sum + projNota,
                                        count: current.count + 1
                                    });
                                });
                            }
                        }
                    });
                }

                raGrades.sort((a, b) => a.nom.localeCompare(b.nom));
                s.raGrades = raGrades;

                const projectGrades = aulaProjects.map(p => {
                    const info = projectGradesMap.get(p.id);
                    return {
                        id: p.id,
                        nom: p.nom,
                        nota: info && info.count > 0 ? (info.sum / info.count) : null,
                        estat: p.estat
                    };
                });

                const validGrades = projectGrades.filter(pg => pg.nota !== null);
                const avg = validGrades.length > 0
                    ? validGrades.reduce((acc, pg) => acc + (pg.nota || 0), 0) / validGrades.length
                    : 0;

                s.projectGrades = projectGrades;
                s.averageGrade = avg;
                s.avatar = getAvatarSrc(s.avatar || avatars[String(s.id)]);
            });

            alumnes.sort((a, b) => (b.averageGrade || 0) - (a.averageGrade || 0));

            setClassroomData({ alumnes, projectes: aulaProjects });

        } catch (error) {
            console.error(error);
            message.error("Error cargando datos del aula");
        } finally {
            setLoadingData(false);
        }
    };

    const handleSaveLayout = async (positionsToSave?: Record<string, { x: number; y: number }>) => {
        if (!selectedAula) return;
        const toSave = positionsToSave || cardPositions;
        setSavingLayout(true);
        try {
            const success = await PostClassroomLayout(selectedAula, toSave);
            if (success) {
                message.success("Disposició de l'aula guardada correctament");
            } else {
                message.error("Error al guardar la disposició");
            }
        } catch (e) {
            console.error("Error guardant layout aula:", e);
            message.error("Error al guardar la disposició");
        } finally {
            setSavingLayout(false);
        }
    };

    const handleResetLayout = () => {
        setCardPositions({});
        if (selectedAula) {
            PostClassroomLayout(selectedAula, {});
            message.info("Disposició restablerta");
        }
    };

    const fetchAvatars = async () => {
        try {
            const rankings = await GetRankingsUsuariId(userId);
            if (rankings) {
                const rankingData = Array.isArray(rankings) ? rankings : rankings.ranking || [];
                const map: Record<string, string> = {};
                rankingData.forEach((r: any) => {
                    const uid = r.id_usuari || r.id;
                    if (uid && r.avatar) {
                        map[String(uid)] = r.avatar;
                    }
                });
                setAvatars(map);
            }
        } catch (e) {
            console.error("Error fetching avatars", e);
        }
    };

    const fetchStudentData = async () => {
        setLoadingData(true);
        try {
            const userIdStr = userId ? userId.toString() : "";
            const [alumneData, validacionsResult] = await Promise.all([
                GetAlumneId(userId),
                GetAlumnesValidacioByProjectV2("0", userIdStr)
            ]);

            if (alumneData && alumneData.projectes) {
                const filteredProjects = alumneData.projectes.filter((p: any) => p.estat === 1 || p.estat === 2);

                const projectGradesMap = new Map<number, { sum: number, count: number }>();

                if (Array.isArray(validacionsResult)) {
                    validacionsResult.forEach((v: any) => {
                        let parsedProjs = v.projectes;
                        if (typeof v.projectes === 'string') {
                            try {
                                parsedProjs = JSON.parse(v.projectes);
                            } catch (e) {
                                parsedProjs = [];
                            }
                        }

                        if (Array.isArray(parsedProjs)) {
                            parsedProjs.forEach((p: any) => {
                                const projId = Number(p.id_projecte);
                                const projNota = p.nota !== null && p.nota !== undefined && p.nota !== "" ? Number(p.nota) : 0;
                                const current = projectGradesMap.get(projId) || { sum: 0, count: 0 };
                                projectGradesMap.set(projId, {
                                    sum: current.sum + projNota,
                                    count: current.count + 1
                                });
                            });
                        }
                    });
                }

                const grades = filteredProjects.map((p: any) => {
                    const info = projectGradesMap.get(p.id);
                    return {
                        id: p.id,
                        nom: p.nom,
                        nota: info && info.count > 0 ? (info.sum / info.count) : null,
                        estat: p.estat
                    };
                });

                const validGrades = grades.filter((g: any) => g.nota !== null);
                const avg = validGrades.length > 0
                    ? validGrades.reduce((acc: number, g: any) => acc + (g.nota || 0), 0) / validGrades.length
                    : 0;

                setMyProjects(filteredProjects);
                setMyProjectGrades(grades);
                setMyAverageGrade(avg);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingData(false);
        }
    };

    // --- Actions ---

    const handleImpersonate = async (targetId: string) => {
        try {
            const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost';
            message.loading("Impersonando usuario...");

            const response = await fetch(`${baseUrl}:8061/login/impersonate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({ adminId: userId, targetId })
            });

            if (response.ok) {
                const data = await response.json();
                const { token, permisos } = data;

                let alu = DefaultAulumneInfo;
                if (permisos && permisos.length > 0) {
                    const res = await GetAlumneId(permisos[0].id, token);
                    if (res) alu = res;
                }

                if (!localStorage.getItem('adminToken')) {
                    localStorage.setItem('adminToken', localStorage.getItem('authToken') || '');
                    localStorage.setItem('admin_userId', localStorage.getItem('userId') || '');
                    localStorage.setItem('admin_user', localStorage.getItem('user') || '');
                    localStorage.setItem('admin_userPermisos', localStorage.getItem('userPermisos') || '');
                    localStorage.setItem('admin_alumneInfo', localStorage.getItem('alumneInfo') || '');
                }

                localStorage.setItem('authToken', token);

                setLoggedIn(true);
                setUserId(String(permisos[0].id));
                setUser(permisos[0]);
                setUserPermisos(permisos);
                setAlumneInfo(alu);

                message.success('Impersonando...');
                window.location.reload();

            } else {
                message.error('Error al impersonar');
            }
        } catch (e) {
            console.error(e);
            message.error('Error de conexión');
        }
    };

    const handleStudentClick = (student: StudentData) => {
        setSelectedStudent(student);
        setModalVisible(true);
    };


    // --- Helpers ---

    const getGradeColor = (grade: number) => {
        if (grade >= 9) return '#52c41a'; // Excelente
        if (grade >= 7) return '#1890ff'; // Notable
        if (grade >= 5) return '#faad14'; // Bien
        return '#f5222d'; // Suspenso
    };

    const isStudent = user && user.tipus !== "0" && user.tipus !== "1";

    const renderAdminDashboard = () => {
        const stats = {
            totalStudents: classroomData?.alumnes.length || 0,
            passed: classroomData?.alumnes.filter(s => (s.averageGrade || 0) >= 5).length || 0,
            avgGrade: classroomData?.alumnes.length ?
                (classroomData.alumnes.reduce((acc, s) => acc + (s.averageGrade || 0), 0) / classroomData.alumnes.length) : 0
        };

        return (
            <div style={{ padding: 24 }}>
                <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                        <Title level={3} style={{ margin: 0 }}>Dashboard Avaluació</Title>
                        {aulas.length > 0 && (
                            <Select
                                value={selectedAula}
                                style={{ width: 250 }}
                                onChange={setSelectedAula}
                                loading={loadingData}
                                placeholder="Selecciona una aula"
                                size="large"
                            >
                                {aulas.map(a => <Option key={a.id} value={a.id}>{a.nom}</Option>)}
                            </Select>
                        )}

                        {selectedAula && (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <Select
                                    value={classroomViewMode}
                                    onChange={(val) => setClassroomViewMode(val)}
                                    size="large"
                                    style={{ width: 220 }}
                                    dropdownMatchSelectWidth={false}
                                >
                                    <Option value="aula">
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <DragOutlined style={{ color: '#1890ff' }} /> Vista Aula (Lliure)
                                        </span>
                                    </Option>
                                    <Option value="graella">
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <AppstoreOutlined style={{ color: '#722ed1' }} /> Vista Graella
                                        </span>
                                    </Option>
                                </Select>

                                {classroomViewMode === 'aula' && (
                                    <Button
                                        icon={<UndoOutlined />}
                                        onClick={handleResetLayout}
                                        size="large"
                                        title="Restablir posicions inicials"
                                    />
                                )}
                            </div>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => selectedAula && fetchClassroomStats(selectedAula)}
                            loading={loadingData}
                            size="large"
                        >
                            Refrescar
                        </Button>
                        <Button
                            icon={<SettingOutlined />}
                            onClick={() => setViewMode('config')}
                            size="large"
                        >
                            Gestionar
                        </Button>
                    </div>
                </div>

                {loadingData ? <div style={{ textAlign: 'center', marginTop: 50 }}><Spin size="large" /></div> : (
                    <>
                        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                            {/* Stats Column */}
                            <Col xs={24} md={12} lg={14}>
                                <Row gutter={[16, 16]}>
                                    <Col span={12}>
                                        <Card bordered={false} style={{ borderRadius: 12, height: '100%' }}>
                                            <Statistic title="Alumnes Totals" value={stats.totalStudents} prefix={<UserOutlined />} />
                                        </Card>
                                    </Col>
                                    <Col span={12}>
                                        <Card bordered={false} style={{ borderRadius: 12, height: '100%' }}>
                                            <Statistic
                                                title="Aprovats (Mitjana)"
                                                value={stats.passed}
                                                suffix={`/ ${stats.totalStudents}`}
                                                valueStyle={{ color: '#3f8600' }}
                                                prefix={<CheckCircleOutlined />}
                                            />
                                        </Card>
                                    </Col>
                                    <Col span={24}>
                                        <Card bordered={false} style={{ borderRadius: 12, marginBottom: 0 }}>
                                            <Statistic
                                                title="Mitjana Global Aula"
                                                value={stats.avgGrade}
                                                precision={2}
                                                prefix={<ReadOutlined />}
                                                valueStyle={{ color: getGradeColor(stats.avgGrade) }}
                                            />
                                        </Card>
                                    </Col>
                                </Row>
                            </Col>

                            {/* Stacked Chart */}
                            <Col xs={24} md={12} lg={10}>
                                <Card
                                    title="Rendimiento por Proyecto"
                                    bordered={false}
                                    style={{ borderRadius: 12, height: '100%', minHeight: 250 }}
                                    bodyStyle={{ padding: 20, height: 260 }}
                                >
                                    {classroomData && (
                                        <ProjectStackedBarChart students={classroomData.alumnes} projects={classroomData.projectes} />
                                    )}
                                </Card>
                            </Col>
                        </Row>

                        {classroomViewMode === 'aula' ? (
                            /* --- FREE CLASSROOM LAYOUT CANVAS --- */
                            <div style={{
                                background: '#ffffff',
                                border: '1px solid #e8e8e8',
                                borderRadius: 16,
                                padding: '24px 20px',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                                marginBottom: 24
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 16,
                                    paddingBottom: 12,
                                    borderBottom: '1px dashed #d9d9d9'
                                }}>
                                    <div>
                                        <Text strong style={{ fontSize: 16, color: '#262626' }}>🏫 Distribució de l'Aula (Taules)</Text>
                                        <Text type="secondary" style={{ marginLeft: 12, fontSize: 13 }}>
                                            Arrossega les targetes lliurement per ubicar a cada alumne al seu lloc a l'aula
                                        </Text>
                                    </div>
                                    <Tag color="cyan" style={{ borderRadius: 12, padding: '2px 10px' }}>
                                        Arrossegar per moure • Es guarda automàticament
                                    </Tag>
                                </div>

                                <div
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        const cardId = e.dataTransfer.getData("text/plain");
                                        if (!cardId) return;

                                        const offsetXRaw = e.dataTransfer.getData("application/x-offset-x");
                                        const offsetYRaw = e.dataTransfer.getData("application/x-offset-y");
                                        const offsetX = offsetXRaw ? parseFloat(offsetXRaw) : 0;
                                        const offsetY = offsetYRaw ? parseFloat(offsetYRaw) : 0;

                                        const container = e.currentTarget;
                                        const rect = container.getBoundingClientRect();

                                        const cardWidth = cardId === 'teacher' ? 440 : 250;
                                        const rawX = e.clientX - rect.left + container.scrollLeft - offsetX;
                                        const rawY = e.clientY - rect.top + container.scrollTop - offsetY;

                                        const maxX = Math.max(10, (container.scrollWidth || rect.width) - cardWidth - 10);
                                        const maxY = Math.max(10, (container.scrollHeight || 1200) - 150);

                                        const x = Math.max(10, Math.min(maxX, Math.round(rawX)));
                                        const y = Math.max(10, Math.min(maxY, Math.round(rawY)));

                                        const newPositions = { ...cardPositions, [cardId]: { x, y } };
                                        setCardPositions(newPositions);
                                        handleSaveLayout(newPositions);
                                    }}
                                    style={{
                                        position: 'relative',
                                        width: '100%',
                                        minHeight: 900,
                                        background: '#fafbfc',
                                        border: '2px dashed #e6e9ed',
                                        borderRadius: 12,
                                        overflow: 'auto',
                                        backgroundImage: 'radial-gradient(#d3d9e0 1px, transparent 1px)',
                                        backgroundSize: '24px 24px'
                                    }}
                                >
                                    {/* TEACHER DOUBLE DESK CARD */}
                                    {(() => {
                                        const teacherPos = cardPositions['teacher'] || { x: 380, y: 20 };
                                        const teacherUser = user as any;
                                        const teacherAvatar = getAvatarSrc(teacherUser?.avatar || avatars[userId + ""]);
                                        const teacherName = teacherUser?.nom ? `${teacherUser.nom} ${teacherUser.cognoms || ''}` : 'Profesor';

                                        return (
                                            <div
                                                draggable
                                                onDragStart={(e) => {
                                                    e.dataTransfer.setData("text/plain", "teacher");
                                                    const cardRect = e.currentTarget.getBoundingClientRect();
                                                    e.dataTransfer.setData("application/x-offset-x", (e.clientX - cardRect.left).toString());
                                                    e.dataTransfer.setData("application/x-offset-y", (e.clientY - cardRect.top).toString());
                                                }}
                                                style={{
                                                    position: 'absolute',
                                                    left: teacherPos.x,
                                                    top: teacherPos.y,
                                                    width: 440,
                                                    cursor: 'grab',
                                                    zIndex: 20,
                                                    transition: 'box-shadow 0.2s'
                                                }}
                                            >
                                                <Card
                                                    size="small"
                                                    bordered={false}
                                                    style={{
                                                        borderRadius: 14,
                                                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                                        color: '#ffffff',
                                                        boxShadow: '0 6px 18px rgba(24, 144, 255, 0.35)',
                                                        border: '2px solid #69c0ff'
                                                    }}
                                                    bodyStyle={{ padding: '14px 18px' }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                                            <Avatar
                                                                size={54}
                                                                src={teacherAvatar}
                                                                style={{
                                                                    border: '2px solid #ffffff',
                                                                    backgroundColor: '#003a8c',
                                                                    color: '#fff',
                                                                    fontWeight: 'bold',
                                                                    fontSize: 20
                                                                }}
                                                            >
                                                                {teacherName[0]}
                                                            </Avatar>
                                                            <div>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                    <Tag color="#52c41a" style={{ color: '#fff', fontWeight: 'bold', border: 'none', borderRadius: 4 }}>
                                                                        TAULA PROFESSOR
                                                                    </Tag>
                                                                    <Tag color="#fff" style={{ color: '#096dd9', fontWeight: 600, border: 'none', borderRadius: 4 }}>
                                                                        DOCENT
                                                                    </Tag>
                                                                </div>
                                                                <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 4, color: '#ffffff' }}>
                                                                    {teacherName}
                                                                </div>
                                                                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>
                                                                    {(user as any)?.email || 'Sessió docent activa'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                            <DragOutlined style={{ fontSize: 20, color: 'rgba(255,255,255,0.8)' }} />
                                                            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>Moure taula</span>
                                                        </div>
                                                    </div>
                                                </Card>
                                            </div>
                                        );
                                    })()}

                                    {/* STUDENT DESK CARDS */}
                                    {classroomData?.alumnes.map((student, index) => {
                                        const avatarUrl = getAvatarSrc(avatars[student.id] || student.avatar);
                                        const initials = (student.nom[0] + (student.cognoms ? student.cognoms[0] : '')).toUpperCase();
                                        const avatarColor = stringToColor(student.nom + (student.cognoms || ''));

                                        const cols = 4;
                                        const colIndex = index % cols;
                                        const rowIndex = Math.floor(index / cols);
                                        const defaultX = 40 + colIndex * 270;
                                        const defaultY = 160 + rowIndex * 190;

                                        const pos = cardPositions[student.id + ""] || { x: defaultX, y: defaultY };

                                        return (
                                            <div
                                                key={student.id}
                                                draggable
                                                onDragStart={(e) => {
                                                    e.dataTransfer.setData("text/plain", student.id + "");
                                                    const cardRect = e.currentTarget.getBoundingClientRect();
                                                    e.dataTransfer.setData("application/x-offset-x", (e.clientX - cardRect.left).toString());
                                                    e.dataTransfer.setData("application/x-offset-y", (e.clientY - cardRect.top).toString());
                                                }}
                                                style={{
                                                    position: 'absolute',
                                                    left: pos.x,
                                                    top: pos.y,
                                                    width: 250,
                                                    cursor: 'grab',
                                                    zIndex: 10,
                                                    transition: 'box-shadow 0.2s'
                                                }}
                                            >
                                                <Card
                                                    hoverable
                                                    size="small"
                                                    style={{
                                                        borderRadius: 12,
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                                                        borderTop: `3px solid ${getGradeColor(student.averageGrade || 0)}`,
                                                        background: '#ffffff'
                                                    }}
                                                    bodyStyle={{ padding: 12 }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                                                        <div onClick={() => handleStudentClick(student)} style={{ cursor: 'pointer', flexShrink: 0 }}>
                                                            <Avatar
                                                                size={42}
                                                                src={avatarUrl}
                                                                style={{
                                                                    backgroundColor: avatarColor,
                                                                    verticalAlign: 'middle',
                                                                    border: '1px solid #f0f0f0'
                                                                }}
                                                            >
                                                                {!avatarUrl && initials}
                                                            </Avatar>
                                                        </div>
                                                        <div style={{ marginLeft: 10, flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                                                            <Text
                                                                strong
                                                                onClick={() => handleStudentClick(student)}
                                                                style={{
                                                                    display: 'block',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    fontSize: 13,
                                                                    lineHeight: 1.2,
                                                                    cursor: 'pointer',
                                                                    marginBottom: 2
                                                                }}
                                                                title={`${student.nom} ${student.cognoms}`}
                                                            >
                                                                {student.nom} {student.cognoms}
                                                            </Text>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <Tag color={student.averageGrade && student.averageGrade >= 5 ? "success" : "warning"} style={{ margin: 0, fontSize: 10, borderRadius: 8, padding: '0 4px' }}>
                                                                    Nota: {(student.averageGrade || 0).toFixed(1)}
                                                                </Tag>
                                                                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                                    <Tooltip title="Impersonar Alumno">
                                                                        <UserSwitchOutlined
                                                                            onClick={(e) => { e.stopPropagation(); handleImpersonate(student.id.toString()); }}
                                                                            style={{ cursor: 'pointer', color: '#1890ff', fontSize: 14 }}
                                                                        />
                                                                    </Tooltip>
                                                                    <DragOutlined style={{ color: '#bfbfbf', fontSize: 12 }} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* RA Indicators Grid */}
                                                    {student.raGrades && student.raGrades.length > 0 && (
                                                        <div style={{ marginBottom: 6 }}>
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '3px' }}>
                                                                {student.raGrades.map((ra, idx) => {
                                                                    const finalVal = ra.nota !== null ? (ra.nota < 5 ? Math.floor(ra.nota) : Math.round(ra.nota)) : null;
                                                                    return (
                                                                        <Tooltip key={idx} title={`RA-${ra.nom}: ${ra.nota !== null ? ra.nota.toFixed(2) : 'N/P'}`}>
                                                                            <div style={{
                                                                                height: 18, borderRadius: 3,
                                                                                backgroundColor: finalVal !== null ? getGradeColor(finalVal) : '#f0f0f0',
                                                                                color: finalVal !== null ? 'white' : '#999', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                                fontSize: 9, fontWeight: 'bold', border: '1px solid #d9d9d9',
                                                                                cursor: 'pointer'
                                                                            }} onClick={() => handleStudentClick(student)}>
                                                                                {finalVal !== null ? finalVal : '-'}
                                                                            </div>
                                                                        </Tooltip>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Project Indicators Grid */}
                                                    {student.projectGrades && student.projectGrades.length > 0 && (
                                                        <div>
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '3px' }}>
                                                                {student.projectGrades.slice(0, 8).map((pg, idx) => {
                                                                    const finalVal = pg.nota !== null ? (pg.nota < 5 ? Math.floor(pg.nota) : Math.round(pg.nota)) : null;
                                                                    return (
                                                                        <Tooltip key={idx} title={`${pg.nom}: ${pg.nota !== null ? pg.nota.toFixed(2) : 'N/P'}`}>
                                                                            <div style={{
                                                                                height: 18, borderRadius: 3,
                                                                                backgroundColor: finalVal !== null ? getGradeColor(finalVal) : '#f0f0f0',
                                                                                color: finalVal !== null ? 'white' : '#999', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                                fontSize: 9, fontWeight: 'bold', border: '1px solid #d9d9d9',
                                                                                cursor: 'pointer'
                                                                            }} onClick={() => handleStudentClick(student)}>
                                                                                {finalVal !== null ? finalVal : '-'}
                                                                            </div>
                                                                        </Tooltip>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                </Card>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            /* --- VISTA GRAELLA --- */
                            <div style={{ display: 'flex', gap: 24, minHeight: 'calc(100vh - 400px)' }}>
                                <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>
                                    <Row gutter={[16, 16]}>
                                        {classroomData?.alumnes.map((student) => {
                                            const avatarUrl = getAvatarSrc(avatars[student.id] || student.avatar);
                                            const initials = (student.nom[0] + (student.cognoms ? student.cognoms[0] : '')).toUpperCase();
                                            const avatarColor = stringToColor(student.nom + (student.cognoms || ''));
                                            return (
                                                <Col key={student.id} xs={24} sm={12} md={8} lg={6} xl={4}>
                                                    <Card
                                                        hoverable
                                                        size="small"
                                                        style={{
                                                            borderRadius: 12,
                                                            height: '100%',
                                                            borderTop: `3px solid ${getGradeColor(student.averageGrade || 0)}`,
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                                        }}
                                                        bodyStyle={{ padding: 12 }}
                                                        onClick={() => handleStudentClick(student)}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                                                            <div style={{ flexShrink: 0 }}>
                                                                <Avatar
                                                                    src={avatarUrl}
                                                                    size={42}
                                                                    style={{
                                                                        backgroundColor: avatarColor,
                                                                        verticalAlign: 'middle',
                                                                        border: '1px solid #f0f0f0'
                                                                    }}
                                                                >
                                                                    {!avatarUrl && initials}
                                                                </Avatar>
                                                            </div>
                                                            <div style={{ marginLeft: 10, flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                                                                <div style={{ fontWeight: 'bold', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2, marginBottom: 4 }} title={`${student.nom} ${student.cognoms}`}>
                                                                    {student.nom} {student.cognoms}
                                                                </div>
                                                                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                                                    <Tooltip title="Impersonar">
                                                                        <UserSwitchOutlined
                                                                            onClick={(e) => { e.stopPropagation(); handleImpersonate(student.id.toString()); }}
                                                                            style={{ cursor: 'pointer', color: '#1890ff', fontSize: 14 }}
                                                                        />
                                                                    </Tooltip>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* RA Indicators Grid */}
                                                        {student.raGrades && student.raGrades.length > 0 && (
                                                            <div style={{ marginBottom: 8 }}>
                                                                <Text style={{ fontSize: 10, color: '#999', display: 'block', marginBottom: 4 }}>Resultats d'Aprenentatge:</Text>
                                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px' }}>
                                                                    {student.raGrades.map((ra, idx) => {
                                                                        const finalVal = ra.nota !== null ? (ra.nota < 5 ? Math.floor(ra.nota) : Math.round(ra.nota)) : null;
                                                                        return (
                                                                            <Tooltip key={idx} title={`RA-${ra.nom}: ${ra.nota !== null ? ra.nota.toFixed(2) : 'N/P'}`}>
                                                                                <div style={{
                                                                                    height: 22, borderRadius: 4,
                                                                                    backgroundColor: finalVal !== null ? getGradeColor(finalVal) : '#f0f0f0',
                                                                                    color: finalVal !== null ? 'white' : '#999', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                                    fontSize: 10, fontWeight: 'bold', border: '1px solid #d9d9d9'
                                                                                }}>
                                                                                    {finalVal !== null ? finalVal : '-'}
                                                                                </div>
                                                                            </Tooltip>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Project Indicators Grid */}
                                                        {student.projectGrades && student.projectGrades.length > 0 && (
                                                            <div>
                                                                <Text style={{ fontSize: 10, color: '#999', display: 'block', marginBottom: 4 }}>Projectes:</Text>
                                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px' }}>
                                                                    {student.projectGrades.slice(0, 8).map((pg, idx) => {
                                                                        const finalVal = pg.nota !== null ? (pg.nota < 5 ? Math.floor(pg.nota) : Math.round(pg.nota)) : null;
                                                                        return (
                                                                            <Tooltip key={idx} title={`${pg.nom}: ${pg.nota !== null ? pg.nota.toFixed(2) : 'N/P'}`}>
                                                                                <div style={{
                                                                                    height: 22, borderRadius: 4,
                                                                                    backgroundColor: finalVal !== null ? getGradeColor(finalVal) : '#f0f0f0',
                                                                                    color: finalVal !== null ? 'white' : '#999', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                                    fontSize: 10, fontWeight: 'bold', border: '1px solid #d9d9d9'
                                                                                }}>
                                                                                    {finalVal !== null ? finalVal : '-'}
                                                                                </div>
                                                                            </Tooltip>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Card>
                                                </Col>
                                            );
                                        })}
                                        {(!classroomData || classroomData.alumnes.length === 0) && (
                                            <div style={{ width: '100%', textAlign: 'center', padding: 40 }}>
                                                <Empty description="No hay alumnos en esta aula" />
                                            </div>
                                        )}
                                    </Row>
                                </div>
                            </div>
                        )}
                    </>
                )}
                <StudentDetailModal
                    visible={modalVisible}
                    onClose={() => setModalVisible(false)}
                    student={selectedStudent}
                />
            </div>
        );
    };



    const renderStudentDashboard = () => {
        const columns = [
            { title: 'Proyecto', dataIndex: 'nom', key: 'nom', render: (text: string) => <b>{text}</b> },
            {
                title: 'Nota',
                dataIndex: 'nota',
                key: 'nota',
                align: 'right' as const,
                render: (val: number | null) => {
                    if (val === null) return <Tag>N/P</Tag>;
                    const color = val >= 5 ? 'green' : 'red';
                    return (
                        <Tag color={color} style={{ fontSize: 14, padding: '4px 10px' }}>{val.toFixed(2)}</Tag>
                    );
                }
            },
            {
                title: 'Nota Final',
                dataIndex: 'nota',
                key: 'nota_final',
                align: 'center' as const,
                render: (val: number | null) => {
                    if (val === null) return <Tag>N/P</Tag>;
                    const finalVal = val < 5 ? Math.floor(val) : Math.round(val);
                    const color = finalVal >= 5 ? 'green' : 'red';
                    return (
                        <Tag color={color} style={{ fontSize: 14, padding: '4px 10px', fontWeight: 'bold' }}>{finalVal}</Tag>
                    );
                }
            },
            {
                title: 'Estado',
                dataIndex: 'estat',
                key: 'estat',
                render: (estat: number) => {
                    let color = 'default';
                    let text = 'Desconocido';
                    if (estat === 1) { color = 'green'; text = 'En curso'; }
                    if (estat === 2) { color = 'orange'; text = 'Finalizado'; }
                    if (estat === 3) { color = 'red'; text = 'Cerrado'; }
                    return <Tag color={color}>{text}</Tag>;
                }
            }
        ];

        const avgFinal = myAverageGrade < 5 ? Math.floor(myAverageGrade) : Math.round(myAverageGrade);

        return (
            <div style={{ padding: 24 }}>
                <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Title level={3}>Resumen de Notas</Title>
                    <Button icon={<ReloadOutlined />} onClick={fetchStudentData}>Refrescar</Button>
                </div>

                {loadingData ? <div style={{ textAlign: 'center', padding: 50 }}><Spin size="large" /></div> : (
                    <Row gutter={[24, 24]}>
                        <Col xs={24} md={8} lg={6}>
                            <Card
                                hoverable
                                size="small"
                                style={{ borderRadius: 8, height: '100%', borderLeft: `4px solid ${getGradeColor(myAverageGrade || 0)}` }}
                                bodyStyle={{ padding: 12 }}
                            >
                                <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                                    <Avatar size={64} src={getAvatarSrc(alumne?.alumne?.avatar || avatars[userId + ""])} style={{ backgroundColor: AvaluacioColors.colors.color3 }}>{alumne?.alumne?.nom ? alumne.alumne.nom[0] : 'U'}</Avatar>
                                    <div style={{ flex: 1, overflow: 'hidden' }}>
                                        <div style={{ fontWeight: 'bold', fontSize: 16, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {alumne?.alumne?.nom} {alumne?.alumne?.cognoms}
                                        </div>
                                        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            <Tag color="cyan" style={{ fontSize: 12, padding: '2px 6px', width: 'fit-content' }}>AVG: {(myAverageGrade || 0).toFixed(2)}</Tag>
                                            <Tag color={avgFinal >= 5 ? 'green' : 'red'} style={{ fontSize: 12, padding: '2px 6px', fontWeight: 'bold', width: 'fit-content' }}>NOTA FINAL: {avgFinal}</Tag>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                    {myProjectGrades.map((pg, idx) => (
                                        <Tooltip key={idx} title={`${pg.nom}: ${pg.nota !== null ? pg.nota.toFixed(2) : 'N/P'}`}>
                                            <div style={{
                                                width: 16,
                                                height: 16,
                                                borderRadius: 2,
                                                backgroundColor: pg.nota !== null ? getGradeColor(pg.nota) : '#f0f0f0',
                                                border: '1px solid #d9d9d9'
                                            }} />
                                        </Tooltip>
                                    ))}
                                </div>
                            </Card>
                        </Col>
                        <Col xs={24} md={16} lg={18}>
                            <Card title="Detalle de Calificaciones (Sobre 10)" bordered={false} style={{ borderRadius: 8 }}>
                                <Table
                                    columns={columns}
                                    dataSource={myProjectGrades}
                                    rowKey="id"
                                    pagination={false}
                                    bordered
                                />
                            </Card>
                        </Col>
                    </Row>
                )}
            </div>
        );
    }

    if (!user) return <div />;

    return (
        <Layout style={{ flexDirection: 'row', minHeight: '100%' }}>
            <AppSider currentVal={0} setPantalla={() => { }} />
            <Content style={{
                padding: 0,
                minHeight: 'calc(100vh - 64px)',
                backgroundColor: '#f0f2f5',
                overflow: 'hidden', // Prevent duplicate scrollbars
                width: '100%'
            }}>
                <div style={{ height: '100%', overflowY: 'auto' }}>
                    {(user.tipus === "1" || user.tipus === "0") && viewMode === 'config' ? (
                        <div style={{ padding: 24 }}>
                            <div style={{ marginBottom: 16 }}>
                                <Button onClick={() => setViewMode('dashboard')}>Volver al Dashboard</Button>
                            </div>
                            <Configuracio />
                        </div>
                    ) : (user.tipus === "1" || user.tipus === "0") ? (
                        renderAdminDashboard()
                    ) : (
                        renderStudentDashboard()
                    )}
                </div>
            </Content>
        </Layout>
    );
};

export default Dashboard;
